import { useState } from 'react';
import { Icoon } from '../ui/Icoon';
import { api } from '../lib/api';
import { NIVEAUS, useActie, useData, type TemplateRij } from '../lib/data';
import { getal, Laden, Modal, Veld, VerwijderKnop } from '../ui/ui';

export function Templates() {
  const { data: d } = useData();
  const [sel, setSel] = useState<string | null>(null);
  const [form, setForm] = useState<{ t?: TemplateRij; soort: 'WERKPAKKET' | 'TAKENPAKKET' } | null>(null);
  const volgorde = useActie((ids: string[]) => api.post('/api/templates/volgorde', { ids }));
  const verwijder = useActie((id: string) => api.del(`/api/templates/${id}`), { succes: 'Template verwijderd' });
  if (!d) return <Laden />;
  const wps = d.templates.filter((t) => t.soort === 'WERKPAKKET').sort((a, b) => a.volgorde - b.volgorde);
  const gekozen = wps.find((w) => w.id === sel) ?? null;
  const kinderen = d.templates.filter((t) => t.soort === 'TAKENPAKKET' && t.ouderId === (gekozen?.id ?? null)).sort((a, b) => a.volgorde - b.volgorde);
  const schuif = (i: number, r: number) => {
    const ids = wps.map((w) => w.id);
    [ids[i], ids[i + r]] = [ids[i + r], ids[i]];
    volgorde.mutate(ids);
  };
  return (
    <div className="pagina" style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 480px) 1fr', gap: 16, alignItems: 'start' }}>
      <div className="paneel kolom">
        <div className="rij">
          <h1 className="groei">Templates</h1>
          <button className="knop primair klein" onClick={() => setForm({ soort: 'WERKPAKKET' })}>+ Werkpakket-template</button>
        </div>
        <p className="klein mute">Standaard-werkpakketten met hun takenpakketten. De deadline staat in dagen ten opzichte van de livegang; bij gebruik rekent de app de datums uit.</p>
        {wps.map((w, i) => (
          <div key={w.id} className="paneel vlak" style={{ cursor: 'pointer', borderColor: sel === w.id ? 'var(--accent)' : undefined, background: sel === w.id ? 'var(--accent-bg)' : undefined }} onClick={() => setSel(w.id)}>
            <div className="rij">
              <b className="groei">{i + 1}. {w.naam}</b>
              <button className="icoon-knop" disabled={i === 0} onClick={(e) => { e.stopPropagation(); schuif(i, -1); }}>˄</button>
              <button className="icoon-knop" disabled={i === wps.length - 1} onClick={(e) => { e.stopPropagation(); schuif(i, 1); }}>˅</button>
              <button className="icoon-knop" onClick={(e) => { e.stopPropagation(); setForm({ t: w, soort: 'WERKPAKKET' }); }} title="Bewerken"><Icoon naam="pencil" maat={16} /></button>
            </div>
            <div className="klein mute">
              {w.dagenTovLivegang != null ? `${Math.abs(w.dagenTovLivegang)} dagen ${w.dagenTovLivegang < 0 ? 'vóór' : 'na'} livegang` : 'geen deadline'}
              {w.naVorige ? ' · ↪ na vorige' : ''}
            </div>
          </div>
        ))}
        <div className="paneel vlak" style={{ cursor: 'pointer', borderColor: sel === null ? 'var(--accent)' : undefined }} onClick={() => setSel(null)}>
          <b>Losse takenpakket-templates</b>
          <div className="klein mute">In elk werkpakket te gebruiken</div>
        </div>
      </div>
      <div className="paneel">
        <div className="sectie-kop">
          <h2 className="groei">{gekozen ? gekozen.naam : 'Losse takenpakketten'}</h2>
          <button className="knop primair klein" onClick={() => setForm({ soort: 'TAKENPAKKET' })}>+ Takenpakket</button>
        </div>
        <table className="tabel">
          <thead><tr><th>#</th><th>Takenpakket</th><th>Expertise</th><th className="rechts">Uren</th><th>Niveau</th><th /></tr></thead>
          <tbody>
            {kinderen.map((t, i) => (
              <tr key={t.id}>
                <td>{i + 1}</td>
                <td>{t.naam}{t.naVorige ? <span className="klein mute"> ↪ na vorige</span> : null}</td>
                <td>{t.expertise ?? '–'}</td>
                <td className="rechts">{t.uren}</td>
                <td className="klein">{t.minNiveau ? `≥ ${t.minNiveau}%` : ''}{t.maxNiveau < 100 ? ` ≤ ${t.maxNiveau}%` : ''}</td>
                <td className="nowrap">
                  <button className="icoon-knop" onClick={() => setForm({ t, soort: 'TAKENPAKKET' })} title="Bewerken"><Icoon naam="pencil" maat={16} /></button>
                  <VerwijderKnop klein label="🗑" onBevestig={() => verwijder.mutate(t.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!kinderen.length && <div className="leeg">Nog geen takenpakketten.</div>}
        {gekozen && <div style={{ marginTop: 12 }}><VerwijderKnop label="Werkpakket-template verwijderen" onBevestig={() => { verwijder.mutate(gekozen.id); setSel(null); }} /></div>}
      </div>
      {form && <TemplateForm {...form} ouderId={form.soort === 'TAKENPAKKET' ? (form.t?.ouderId ?? gekozen?.id ?? null) : null} onSluit={() => setForm(null)} />}
    </div>
  );
}

function TemplateForm({ t, soort, ouderId, onSluit }: { t?: TemplateRij; soort: 'WERKPAKKET' | 'TAKENPAKKET'; ouderId: string | null; onSluit: () => void }) {
  const { data: d } = useData();
  const [f, setF] = useState({
    naam: t?.naam ?? '',
    dagen: t?.dagenTovLivegang != null ? String(t.dagenTovLivegang) : '',
    naVorige: t?.naVorige ?? false,
    expertise: t?.expertise ?? '',
    uren: t ? String(t.uren) : '',
    minNiveau: t?.minNiveau ?? 0,
    maxNiveau: t?.maxNiveau ?? 100,
  });
  const opslaan = useActie(
    () => {
      const body = { soort, ouderId, naam: f.naam, dagenTovLivegang: getal(f.dagen), naVorige: f.naVorige, expertise: f.expertise || null, uren: getal(f.uren) ?? 0, minNiveau: f.minNiveau, maxNiveau: f.maxNiveau, volgorde: t?.volgorde };
      return t ? api.put(`/api/templates/${t.id}`, body) : api.post('/api/templates', body);
    },
    { succes: 'Template opgeslagen', opNa: onSluit },
  );
  return (
    <Modal titel={`${t ? 'Bewerken' : 'Nieuw'}: ${soort === 'WERKPAKKET' ? 'werkpakket' : 'takenpakket'}-template`} onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" onClick={() => opslaan.mutate()}>Opslaan</button></>}>
      <div className="grid2">
        <Veld label="Naam *" className="span2"><input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} /></Veld>
        {soort === 'WERKPAKKET' ? (
          <Veld label="Deadline t.o.v. livegang (dagen, negatief = ervoor)"><input value={f.dagen} onChange={(e) => setF({ ...f, dagen: e.target.value })} /></Veld>
        ) : (
          <>
            <Veld label="Expertise">
              <select value={f.expertise} onChange={(e) => setF({ ...f, expertise: e.target.value })}>
                <option value="">– geen –</option>
                {d?.expertises.map((x) => <option key={x.id} value={x.naam}>{x.naam}</option>)}
              </select>
            </Veld>
            <Veld label="Uren"><input value={f.uren} onChange={(e) => setF({ ...f, uren: e.target.value })} /></Veld>
            <Veld label="Minimaal niveau">
              <select value={f.minNiveau} onChange={(e) => setF({ ...f, minNiveau: Number(e.target.value) })}>
                <option value={0}>Geen minimum</option>
                {NIVEAUS.map((n) => <option key={n.p} value={n.p}>{n.label}</option>)}
              </select>
            </Veld>
            <Veld label="Maximaal niveau">
              <select value={f.maxNiveau} onChange={(e) => setF({ ...f, maxNiveau: Number(e.target.value) })}>
                <option value={100}>Geen maximum</option>
                {NIVEAUS.filter((n) => n.p < 100).map((n) => <option key={n.p} value={n.p}>{n.label}</option>)}
              </select>
            </Veld>
          </>
        )}
        <label className="check span2"><input type="checkbox" checked={f.naVorige} onChange={(e) => setF({ ...f, naVorige: e.target.checked })} /> Start pas na de vorige</label>
      </div>
    </Modal>
  );
}
